import { makeAutoObservable } from 'mobx';

import { apiService } from '../services';
import {
    AllFilters,
    Claim,
    ClaimFilterView,
    CLAIMS_TABLE_COLUMN_OPTIONS,
    CLAIMS_TABLE_COLUMN_ORDER,
    ClaimsStatistics,
    ClaimsSummary,
    ClaimsTableColumnId,
    ClaimsTableColumnSetting,
    ClaimStatus,
    ColumnFilterEntry,
    EdiObject,
    FilterOperator,
    PreDefaultQuestionType,
} from '../types/claims';
import { SelectOption } from '../components/ui/core/CoreSelect';
import {
  getClaimsColumnFilterState,
  getClaimsColumnOptions,
  withClaimsColumnFilter,
} from '../components/claims/claimsColumnFilters';
import type MainStore from './MainStore';
import { APP_CONFIG_FLAGS } from '../types/app';
import {
  loadPersistedClaimsFilters,
  savePersistedClaimsFilters,
} from '../components/helpers/claimsFiltersStorage';

export type ClaimTabType = 'all' | 'denied';

export interface OrderBy {
  field: string;
  direction: 'asc' | 'desc';
}

// Pagination constants
const PAGE_SIZE = 50;

export default class ClaimsStore {
  mainStore: MainStore | null = null;

  isStatisticsDataLoading: boolean = true;
  isClaimsDataLoading: boolean = true;
  isLoadingMore: boolean = false;

  statisticsData: ClaimsStatistics | null = null;
  activeTab: ClaimTabType = 'all';
  selectedQuickFilter: string | null = null;
  claimsData: Claim[] | null = null;

  // Filter options
  claimsFilterOptions: Record<string, SelectOption[]> = {};

  // Loaders for filter options
  isOptionsLoading: boolean = true;

  // Pagination state
  currentPage: number = 0;
  hasMoreClaims: boolean = true;
  totalClaimsCount: number = 0;

  // Summary tiles for the whole filtered set (REV-2507). Null while it has never
  // loaded or the last load failed; the tiles hide rather than show stale totals.
  claimsSummary: ClaimsSummary | null = null;
  isClaimsSummaryLoading: boolean = false;
  // Generation token for in-flight summary requests. Not observable: it is
  // bookkeeping, and bumping it must not trigger a render.
  private claimsSummaryRequestId: number = 0;

  // Sorting state - default to updatedAt descending (most recently modified first)
  orderBy: OrderBy | null = { field: 'updatedAt', direction: 'desc' };

  // Filters state
  allFilters: AllFilters | null = null;
  showMissingClaims: boolean = false; // Show claims without 837 CLAIM attachments
  claimKeywordSearch: string = '';

  // Claim filter views
  isClaimFilterViewsLoading: boolean = true;
  claimFilterViews: ClaimFilterView[] = [];
  selectedFilterView: string | null = null;

  // Preview
  claimToPreview: Claim | null = null;
  previewRefreshTrigger: number = 0;
  /** When set, Claims page will load and select this claim (by claim number) and open the panel. Used when redirecting from Sidebar after deleting a claim that was filtered by claim. */
  tempClaimNumber: string | null = null;
  /** Set when an AskReveActionMenu ActionRow is clicked; drives the confirmation preview in ClaimPreview before the question is actually sent. */
  claimActivePredefaultQuestion: PreDefaultQuestionType | null = null;

  // Column settings state (visibility + pin/required), keyed by column id.
  // Defaults come from CLAIMS_TABLE_COLUMN_OPTIONS[].visible / .required.
  columnSettings: ClaimsTableColumnSetting[] = ClaimsStore.getDefaultColumnSettings();

  constructor(mainStore: MainStore) {
    makeAutoObservable(this, {
      mainStore: false,
      claimsSummaryRequestId: false,
    } as any);
    this.mainStore = mainStore;
    this.restorePersistedFilters();
  }

  // The Claims page's filters and column settings are persisted together under one
  // localStorage key per tenant and user (see claimsFiltersStorage). They are restored here and
  // again by MainStore.setFiltersScope once the tenant and user are known: the store outlives
  // the page, so in-memory filters already survive navigation. Saving happens
  // only from the Claims page's own controls (savePersistedFilters callers below);
  // shortcuts that set filters for navigation -- the home page's claim link, the
  // ?claim_number= URL -- use the plain setters and never overwrite what the user saved.
  restorePersistedFilters = () => {
    const persisted = loadPersistedClaimsFilters(this.mainStore?.filtersScope ?? null);
    this.claimKeywordSearch = persisted.claimKeywordSearch;
    this.showMissingClaims = persisted.showMissingClaims;
    this.allFilters = persisted.allFilters;
    if (persisted.orderBy !== undefined) {
      this.orderBy = persisted.orderBy;
    }
    this.selectedFilterView = persisted.selectedFilterView;
    this.columnSettings = ClaimsStore.mergeColumnSettings(persisted.columnSettings);
  };

  private savePersistedFilters(): void {
    savePersistedClaimsFilters(this.mainStore?.filtersScope ?? null, {
      claimKeywordSearch: this.claimKeywordSearch,
      showMissingClaims: this.showMissingClaims,
      allFilters: this.allFilters,
      orderBy: this.orderBy,
      selectedFilterView: this.selectedFilterView,
      columnSettings: this.columnSettings,
    });
  }

  // Default column settings, derived from CLAIMS_TABLE_COLUMN_OPTIONS[].visible / .required
  private static getDefaultColumnSettings(): ClaimsTableColumnSetting[] {
    // Optional chaining guards against circular-import timing (ClaimsStore <-> ClaimsTable)
    return (CLAIMS_TABLE_COLUMN_OPTIONS ?? []).map(col => ({
      id: col.id as ClaimsTableColumnId,
      visible: col.visible !== false,
      required: col.required === true,
    }));
  }

  // Merge saved column settings with defaults to handle new/removed columns
  private static mergeColumnSettings(saved: ClaimsTableColumnSetting[] | null): ClaimsTableColumnSetting[] {
    const defaults = ClaimsStore.getDefaultColumnSettings();
    if (!saved) return defaults;
    const savedById = new Map<string, ClaimsTableColumnSetting>(
      saved
        .filter(setting => setting && typeof setting === 'object')
        .map(setting => [setting.id, setting])
    );
    return defaults.map(def => ({ ...def, ...savedById.get(def.id) }));
  }

  private getColumnSetting = (columnId: string): ClaimsTableColumnSetting | undefined =>
    this.columnSettings.find(setting => setting.id === columnId);

  private updateColumnSetting = (columnId: ClaimsTableColumnId, updates: Partial<Omit<ClaimsTableColumnSetting, 'id'>>) => {
    this.columnSettings = this.columnSettings.map(setting =>
      setting.id === columnId ? { ...setting, ...updates } : setting
    );
    this.savePersistedFilters();
  };

  setColumnVisibility = (columnId: ClaimsTableColumnId, visible: boolean) => {
    // claimNumber is always required
    if (columnId === 'claimNumber') return;
    this.updateColumnSetting(columnId, { visible });
  };

  toggleColumnVisibility = (columnId: ClaimsTableColumnId) => {
    // claimNumber is always required
    if (columnId === 'claimNumber') return;
    this.updateColumnSetting(columnId, { visible: !this.getColumnSetting(columnId)?.visible });
  };

  /** Apply column visibility from a saved filter view (merges with defaults for new columns). */
  applyColumnVisibilityFromSavedView = (saved: Record<string, boolean>) => {
    const defaults = ClaimsStore.getDefaultColumnSettings();
    this.columnSettings = this.columnSettings.map(setting => ({
      ...setting,
      visible: setting.id === 'claimNumber'
        ? true
        : saved[setting.id] ?? defaults.find(def => def.id === setting.id)?.visible ?? true,
    }));
    this.savePersistedFilters();
  };

  isColumnVisible = (columnId: string): boolean => {
    // claimNumber is always visible
    if (columnId === 'claimNumber') return true;
    return this.getColumnSetting(columnId)?.visible !== false;
  };

  toggleRequiredColumn = (columnId: ClaimsTableColumnId) => {
    // claimNumber is always required
    if (columnId === 'claimNumber') return;
    const nextRequired = !this.isColumnRequired(columnId);
    // Marking a hidden column as required makes it visible so it has a visible effect
    this.updateColumnSetting(columnId, nextRequired ? { required: true, visible: true } : { required: false });
  };

  isColumnRequired = (columnId: string): boolean => {
    // claimNumber is always required
    if (columnId === 'claimNumber') return true;
    return !!this.getColumnSetting(columnId)?.required;
  };

  // Visible column ids in the table's display order: pinned (required) columns
  // first, then the rest. Shared by ClaimsTable and the CSV export so the file
  // holds exactly the columns the table shows, in the same order.
  get visibleColumnIds(): ClaimsTableColumnId[] {
    const visible = CLAIMS_TABLE_COLUMN_ORDER.filter((id) => this.isColumnVisible(id));
    return [
      ...visible.filter((id) => this.isColumnRequired(id)),
      ...visible.filter((id) => !this.isColumnRequired(id)),
    ];
  }

  clearClaimsStore = () => {
    this.isStatisticsDataLoading = true;
    this.isClaimsDataLoading = true;
    this.isLoadingMore = false;
    this.statisticsData = null;
    this.claimsData = null;
    this.currentPage = 0;
    this.hasMoreClaims = true;
    this.totalClaimsCount = 0;
    // Drop the tiles with the rows they describe. Kept, they would greet the
    // next visit at full opacity -- reading as current totals -- while the
    // table below is still empty and loading. Bumping the request id also
    // discards any summary still in flight for the filters we just left,
    // so it cannot land after the remount (REV-2507).
    this.claimsSummary = null;
    this.isClaimsSummaryLoading = false;
    this.claimsSummaryRequestId += 1;
    this.isOptionsLoading = true;
    this.tempClaimNumber = null;
    this.claimActivePredefaultQuestion = null;
    // Leaving the Claims page must not leave a previewed claim behind: it is
    // read globally (e.g. ClaimSummaryBar) and would show another claim's
    // details next to the current chat's number (REV-2244).
    this.claimToPreview = null;
  };

  get predefaultQuestions(): PreDefaultQuestionType[] {
    const isDofrAppealAgentEnabled =
      this.mainStore?.appStore?.configFlags?.[
        APP_CONFIG_FLAGS.DOFR_APPEAL_AGENT_ENABLED
      ];

    return [
      {
        question: "Analyze This Claim",
        agent_name: "claim_reviewer",
        placeholder: "To perform the most accurate claim analysis without rework, attach any relevant documents including **correspondence and EOBs**, then press **Send**. **Medical records are optional** but can provide valuable context. <br><br>If you don’t have supporting documents, let me know and I’ll proceed without them.",
      },
      {
        question: "Appeal This Claim",
        agent_name: "appeal_strategy",
        placeholder: "To create an appeal strategy without rework, attach supporting documents - **patient’s medical records, correspondence**, or other relevant materials - then press **Send**. <br><br>If you don’t have supporting documents, let me know and I’ll proceed without them.",
      },
      ...(isDofrAppealAgentEnabled
        ? [
            {
              question: "DOFR Appeal",
              agent_name: "dofr_appeal",
              placeholder: "To create a DOFR appeal without rework, attach any relevant documents including **payer and medical group correspondence** - then press **Send**. <br><br>If you don’t have supporting documents, let me know and I’ll proceed without them.",
            },
          ]
        : []),
    ];
  }

  get activeTabFilters(): AllFilters {
    const base = this.allFilters || {};
    switch (this.activeTab) {
      case 'denied':
        return { ...base, status: { operator: 'and', values: [ClaimStatus.DENIED] } };
      default:
        return base;
    }
  }
  get assignedUsers(): SelectOption[] {
    return this.claimsFilterOptions.assignedUser || [];
  }
  get patients(): SelectOption[] {
    return this.claimsFilterOptions.patientName || [];
  }
  get payers(): SelectOption[] {
    return this.claimsFilterOptions.payer || [];
  }

  get hasFiltersApplied(): boolean {
    return (
      !!this.claimKeywordSearch?.trim() ||
      !!this.allFilters ||
      (!!this.orderBy &&
        !(  // Default sorting
          this.orderBy?.field === "updatedAt" &&
          this.orderBy?.direction === "desc"
        ))
    );
  }

  setIsStatisticsDataLoading = (isLoading: boolean) => {
    this.isStatisticsDataLoading = isLoading;
  };

  setIsClaimsDataLoading = (isLoading: boolean) => {
    this.isClaimsDataLoading = isLoading;
  };

  setStatisticsData = (statisticsData: ClaimsStatistics | null) => {
    this.statisticsData = statisticsData;
  };

  setActiveTab = (tab: ClaimTabType) => {
    this.activeTab = tab;
  };

  setSelectedQuickFilter = (filter: string | null) => {
    this.selectedQuickFilter = filter;
  };

  setClaimsData = (claimsData: Claim[] | null) => {
    this.claimsData = claimsData;
  };

  setIsOptionsLoading = (isLoading: boolean) => {
    this.isOptionsLoading = isLoading;
  };

  setIsLoadingMore = (isLoading: boolean) => {
    this.isLoadingMore = isLoading;
  };

  setClaimsFilterOptions = (claimsFilterOptions: Record<string, SelectOption[]>) => {
    this.claimsFilterOptions = claimsFilterOptions;
  };

  setCurrentPage = (page: number) => {
    this.currentPage = page;
  };

  setHasMoreClaims = (hasMore: boolean) => {
    this.hasMoreClaims = hasMore;
  };

  setTotalClaimsCount = (count: number) => {
    this.totalClaimsCount = count;
  };

  setOrderBy = (orderBy: OrderBy | null) => {
    this.orderBy = orderBy;
    this.savePersistedFilters();
  };

  setAllFilters = (filters: AllFilters | null) => {
    this.allFilters = filters;
    // Reset pagination also
    this.currentPage = 0;
    this.savePersistedFilters();
  };

  setSelectedFilterView = (view: string | null) => {
    this.selectedFilterView = view;
    this.currentPage = 0;
    this.savePersistedFilters();
  };

  setClaimFilterViews = (views: ClaimFilterView[]) => {
    this.claimFilterViews = views;
  };

  setIsClaimFilterViewsLoading = (isLoading: boolean) => {
    this.isClaimFilterViewsLoading = isLoading;
  };

  // Plain setter, for navigation shortcuts (home page claim link, ?claim_number= URL):
  // not saved. The Claims page's search box uses setClaimsPageKeywordSearch.
  setClaimKeywordSearch = (search: string) => {
    this.claimKeywordSearch = search;
  };

  setClaimsPageKeywordSearch = (search: string) => {
    this.setClaimKeywordSearch(search);
    this.savePersistedFilters();
  };

  setShowMissingClaims = (show: boolean) => {
    this.showMissingClaims = show;
    // Reset pagination when toggling
    this.currentPage = 0;
    this.savePersistedFilters();
  };

  setClaimToPreview = (claim: Claim | null) => {
    this.claimToPreview = claim;
  };

  setTempClaimNumber = (claimNumber: string | null) => {
    this.tempClaimNumber = claimNumber;
  };

  setClaimActivePredefaultQuestion = (question: PreDefaultQuestionType | null) => {
    this.claimActivePredefaultQuestion = question;

    // Clear residual data
    if (!question) {
      this.mainStore?.chatStore?.setUploadedFiles([]);
    }
  };

  triggerPreviewRefresh = () => {
    this.previewRefreshTrigger += 1;
  };

  setColumnFilter = (columnId: string, columnValues: string[], operator: FilterOperator = 'and') => {
    this.allFilters = withClaimsColumnFilter(this.allFilters, columnId, columnValues, operator);
    // Reset pagination when filter changes
    this.currentPage = 0;
    this.savePersistedFilters();
  };

  clearColumnFilter = (columnId: string) => {
    if (this.allFilters) {
      const filters = { ...this.allFilters } as Record<string, unknown>;
      delete filters[columnId];
      this.allFilters = Object.keys(filters).length > 0 ? filters as AllFilters : null;
    }
    // Reset pagination when filter changes
    this.currentPage = 0;
    this.savePersistedFilters();
  };

  // Not saved: only used by the home page's claim link (a navigation shortcut).
  clearAllColumnFilters = () => {
    this.allFilters = null;
    this.currentPage = 0;
    this.selectedFilterView = null;
  };

  getColumnFilterValues = (columnId: string): string[] =>
    getClaimsColumnFilterState(this.allFilters, columnId).values;

  getColumnFilterOperator = (columnId: string): FilterOperator =>
    getClaimsColumnFilterState(this.allFilters, columnId).operator;

  isColumnFilterActive = (columnId: string): boolean => {
    if (!this.allFilters) return false;
    const raw = this.allFilters[columnId as keyof AllFilters];
    if (!raw) return false;

    const values = Array.isArray(raw) ? raw : (raw as ColumnFilterEntry).values;
    
    return Array.isArray(values) ? values.length > 0 : true;
  };

  // Filter options for a column (see getClaimsColumnOptions)
  getUniqueColumnValues = (columnId: string): string[] =>
    getClaimsColumnOptions(columnId, {
      filterOptions: this.claimsFilterOptions,
      filters: this.allFilters,
      claims: this.claimsData,
    });

  appendClaimsData = (claims: Claim[]) => {
    if (this.claimsData) {
      this.claimsData = [...this.claimsData, ...claims];
    } else {
      this.claimsData = claims;
    }
  };

  /** Merge fresh values into a loaded claim (table row and preview), e.g. after a claim edit. */
  updateLoadedClaim = (claimId: string, changes: Partial<Claim>) => {
    // Untouched when nothing differs, so observers don't re-render for nothing
    const differs = (claim: Claim) =>
      claim.id === claimId &&
      (Object.keys(changes) as (keyof Claim)[]).some((key) => claim[key] !== changes[key]);
    if (this.claimsData?.some(differs)) {
      this.claimsData = this.claimsData.map((c) => (c.id === claimId ? { ...c, ...changes } : c));
    }
    if (this.claimToPreview && differs(this.claimToPreview)) {
      this.claimToPreview = { ...this.claimToPreview, ...changes };
    }
  };

  /** Flag a loaded claim as Appealed after an appeal is recorded on it. */
  markClaimAppealed = (claimId: string) => {
    this.updateLoadedClaim(claimId, { appealed: true });
  };

  /**
   * Update specific claims in the store after upload.
   * Updates existing claims in place, prepends new claims to the list.
   */
  updateClaimsFromUpload = (uploadedClaims: Claim[]) => {
    if (!uploadedClaims.length) return;

    if (!this.claimsData) {
      // No existing data, just set the uploaded claims
      this.claimsData = uploadedClaims;
      this.totalClaimsCount = uploadedClaims.length;
      return;
    }

    const existingClaimIds = new Set(this.claimsData.map(c => c.id));
    const newClaims: Claim[] = [];
    const updatedClaimMap = new Map<string, Claim>();

    for (const claim of uploadedClaims) {
      if (existingClaimIds.has(claim.id)) {
        // Existing claim - mark for update
        updatedClaimMap.set(claim.id, claim);
      } else {
        // New claim - add to front
        newClaims.push(claim);
      }
    }

    // Update existing claims in place
    const updatedData = this.claimsData.map(claim =>
      updatedClaimMap.has(claim.id) ? updatedClaimMap.get(claim.id)! : claim
    );

    // Prepend new claims
    this.claimsData = [...newClaims, ...updatedData];
    this.totalClaimsCount += newClaims.length;
  };

  resetPagination = (clearData: boolean = true) => {
    this.currentPage = 0;
    this.hasMoreClaims = true;
    if (clearData) {
      this.claimsData = null;
    }
  };

  clearFilters = () => {
    this.allFilters = null;
    this.selectedFilterView = null;
    this.claimKeywordSearch = '';
    this.currentPage = 0;
    this.orderBy = { field: 'updatedAt', direction: 'desc' };
    const defaults = ClaimsStore.getDefaultColumnSettings();
    this.columnSettings = this.columnSettings.map(setting => ({
      ...setting,
      visible: defaults.find(def => def.id === setting.id)?.visible ?? setting.visible,
    }));
    this.savePersistedFilters();
  };

  loadStatisticsData = async () => {
    this.setIsStatisticsDataLoading(true);

    try{
      const statisticsData = await apiService.getStatisticsData();

      this.setStatisticsData(statisticsData);
      this.setIsStatisticsDataLoading(false);
    }catch {
      this.setIsStatisticsDataLoading(false);
      this.setStatisticsData(null);
    }
  };

  setClaimsSummary = (summary: ClaimsSummary | null) => {
    this.claimsSummary = summary;
  };

  setIsClaimsSummaryLoading = (loading: boolean) => {
    this.isClaimsSummaryLoading = loading;
  };

  /** Totals for the whole filtered set (REV-2507).
   *
   * Runs off the same filters/search/missingClaims as loadClaimsData, but
   * independently: a failed summary must not blank the table, and a slow one
   * must not hold up the rows.
   *
   * Typing in the search box or flipping filters fires these faster than they
   * come back, and responses can arrive out of order. Only the newest request
   * may write: an older one landing last would otherwise leave the tiles
   * describing a filter the table is no longer showing, and its `finally`
   * would clear the spinner while a newer request is still in flight. */
  loadClaimsSummary = async () => {
    const requestId = ++this.claimsSummaryRequestId;
    const isCurrent = () => requestId === this.claimsSummaryRequestId;

    this.setIsClaimsSummaryLoading(true);

    try {
      const summary = await apiService.getClaimsSummary(
        this.activeTabFilters,
        this.claimKeywordSearch,
        this.showMissingClaims
      );
      if (isCurrent()) {
        this.setClaimsSummary(summary);
      }
    } catch {
      if (isCurrent()) {
        this.setClaimsSummary(null);
      }
    } finally {
      if (isCurrent()) {
        this.setIsClaimsSummaryLoading(false);
      }
    }
  };

  loadClaimsData = async () => {
    const hasExistingData = this.claimsData !== null && this.claimsData.length > 0;

    // Reset pagination, but keep data visible during refresh
    this.resetPagination(!hasExistingData);
    this.setIsClaimsDataLoading(true);

    // Not awaited: the rows should not wait on the tiles.
    void this.loadClaimsSummary();

    try {
      const response = await apiService.getClaimsData(
        0,
        PAGE_SIZE,
        this.orderBy,
        this.activeTabFilters,
        this.claimKeywordSearch,
        this.showMissingClaims
      )

      this.setClaimsData(response.claims);
      this.setTotalClaimsCount(response.total);
      this.setHasMoreClaims(response.hasMore);
      this.setIsClaimsDataLoading(false);
    } catch {
      this.setIsClaimsDataLoading(false);
      this.setClaimsData(null);
    }
  };

  loadMoreClaims = async () => {
    if (this.isLoadingMore || !this.hasMoreClaims) return;

    this.setIsLoadingMore(true);

    try {
      const response = await apiService.getClaimsData(
        this.currentPage + 1,
        PAGE_SIZE,
        this.orderBy,
        this.activeTabFilters,
        this.claimKeywordSearch,
        this.showMissingClaims
      );

      this.appendClaimsData(response.claims);
      this.setCurrentPage(this.currentPage + 1);
      this.setTotalClaimsCount(response.total);
      this.setHasMoreClaims(response.hasMore);
    } catch {
      this.setHasMoreClaims(false);
    } finally {
      this.setIsLoadingMore(false);
    }
  };

  loadClaimsFilterOptions = async () => {
    try{
      this.setIsOptionsLoading(true);

      const claimsFilterOptions = await apiService.getClaimsFilterOptions();
      
      this.setClaimsFilterOptions(claimsFilterOptions);
      this.setIsOptionsLoading(false);
    }catch {
      this.setClaimsFilterOptions({});
      this.setIsOptionsLoading(false);
    }
  }

  loadClaimFilterViews = async () => {
    this.setIsClaimFilterViewsLoading(true);

    try {
      const response = await apiService.getClaimFilterViews();
      if(response.success && response.data){
        this.setClaimFilterViews(response.data);
        this.setIsClaimFilterViewsLoading(false);
        return;
      }

      throw new Error(response.error || 'Fail to extract claim filter views');
    }catch {
      this.setClaimFilterViews([]);
      this.setIsClaimFilterViewsLoading(false);
    }
  }

  createClaimFiltersView = async (viewName: string, filters: AllFilters) => {
    try {
      const response = await apiService.createClaimFiltersView(viewName, {
        ...(filters || {}),
        orderBy: this.orderBy,
        columnVisibility: Object.fromEntries(this.columnSettings.map(setting => [setting.id, setting.visible])),
      });
      if (response.success && response.data) {
        this.loadClaimFilterViews();
        this.setSelectedFilterView(response.data.id);
        return true
      }

      throw new Error(response.error || 'Failed to create claim filters view');
    } catch (error) {
      throw error;
    }
  }

  updateClaimFiltersView = async (viewId: string, viewName: string, filters: AllFilters) => {
    try {
      const response = await apiService.updateClaimFiltersView(viewId, viewName, {
        ...(filters || {}),
        orderBy: this.orderBy,
        columnVisibility: Object.fromEntries(this.columnSettings.map(setting => [setting.id, setting.visible])),
      });
      if (response.success && response.data) {
        this.loadClaimFilterViews();
        return true
      }

      throw new Error(response.error || 'Failed to update claim filters view');
    } catch (error) {
      throw error;
    }
  }

  deleteClaimFilterView = async (viewId: string): Promise<boolean> => {
    try {
      const response = await apiService.deleteClaimFilterView(viewId);
      if (response.success) {
        this.setClaimFilterViews(
          this.claimFilterViews.filter((v) => v.id !== viewId),
        );
        if (this.selectedFilterView === viewId) {
          this.setSelectedFilterView(null);
          this.setAllFilters(null);
          this.setCurrentPage(0);
          this.loadClaimsData();
        }

        return true;
      }

      return false;
    } catch {
      return false;
    }
  };
}

